import React, { useState } from "react";
import { Sparkles, PanelLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Switch } from "../../../../components/ui/Switch";
import LogoUploader from "../../../../components/logoUploader";
import { showToast } from "../../../../utils/showToast";
import {
  updateLogisticsCompany,
  uploadLogisticsCompanyLogo,
} from "../../../../api/settings";
import { invalidateSidebarBranding } from "../../../../hooks/useSidebarBranding";

// ─────────────────────────────────────────────────────────────────────────────
// WhiteLabelSection — personalización de marca a nivel EMPRESA, visible para
// todos los comercios/clientes de la cuenta.
//
// · Identidad de marca (logo + versión ZPL): el usuario sube una imagen
//   (PNG/JPG/WEBP). El cliente normaliza una copia a ≤320×120 px (el tamaño que
//   admite la etiqueta térmica) y, al guardar, el backend la convierte a ^GFA
//   vía Labelary, sube el original a B2 y persiste { logo, zplLogo }.
// · Co-branding del sidebar: se persiste en `logisticscompanies.sidebarCoBranding`
//   y aplica a todos los usuarios del operador Y a los de los comercios que
//   trabajan con él. Antes vivía en el localStorage de cada navegador, así que
//   no se propagaba a nadie.
//
// El "modo de color" y el "estilo de mapa" viven en /my-account → Apariencia.
// ─────────────────────────────────────────────────────────────────────────────

export default function WhiteLabelSection({ company, companyId, onCompanyUpdate }) {
  const { t } = useTranslation();
  const [savingCoBrand, setSavingCoBrand] = useState(false);
  // El estado vive en el documento de la empresa, no acá: así el switch refleja
  // lo persistido y no una copia que puede quedar desincronizada.
  const coBrand = Boolean(company?.sidebarCoBranding);

  // Guardado optimista con rollback: el switch se mueve al instante y vuelve a
  // su lugar si el PATCH falla (403 por rol, red, etc.). `onCompanyUpdate` es el
  // setState del shell de /admin/settings, que es el dueño del documento.
  const toggleCoBrand = async (on) => {
    if (!companyId || savingCoBrand) return;
    const previo = coBrand;
    onCompanyUpdate?.({ ...company, sidebarCoBranding: on });
    setSavingCoBrand(true);
    try {
      await updateLogisticsCompany(companyId, { sidebarCoBranding: on });
      // Limpia el cache del sidebar para que el cambio se vea sin recargar.
      invalidateSidebarBranding();
    } catch (err) {
      onCompanyUpdate?.({ ...company, sidebarCoBranding: previo });
      const apiMsg = err?.response?.data?.message;
      showToast(
        typeof apiMsg === "string"
          ? apiMsg
          : t("No se pudo guardar la preferencia."),
        false,
      );
    } finally {
      setSavingCoBrand(false);
    }
  };

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <SectionCard
        icon={<Sparkles className="w-4 h-4" aria-hidden="true" />}
        title={t("Identidad de marca")}
        subtitle={t("Logo y elementos visibles para tus comercios y clientes en el portal y en el tracking público.")}
      >
        <LogoUploader
          currentLogo={company?.logo}
          disabled={!companyId}
          onUpload={async ({ logoFile, zplBlob }) => {
            if (!companyId) throw new Error("sin_empresa");
            const res = await uploadLogisticsCompanyLogo(companyId, { logoFile, zplBlob });
            return res?.data ?? res ?? {};
          }}
          onUploaded={(data) => {
            onCompanyUpdate?.({
              ...company,
              logo: data.logo ?? company?.logo,
              zplLogo: data.zplLogo ?? company?.zplLogo,
            });
          }}
        />
      </SectionCard>

      <SectionCard
        icon={<PanelLeft className="w-4 h-4" aria-hidden="true" />}
        title={t("Co-branding del sidebar")}
        subtitle={t("Reemplaza el logo de Beartrack por el de tu empresa en el menú lateral. Aplica a todos los usuarios de tu equipo y de los comercios que trabajan contigo.")}
      >
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-bg px-4 py-3">
          <div className="min-w-0">
            <label htmlFor="cobrand-toggle" className="text-sm font-medium text-fg block cursor-pointer">
              {t("Mostrar solo mi logo en el menú lateral")}
            </label>
            <p className="text-xs text-fg-muted mt-0.5">
              {t("Cuando esté activo, tu equipo y los comercios que trabajan contigo verán el logo de tu empresa en lugar del de Beartrack. El logo de Beartrack solo queda visible con el menú colapsado.")}
            </p>
          </div>
          <Switch
            id="cobrand-toggle"
            checked={coBrand}
            onChange={toggleCoBrand}
            disabled={!company?.logo || !companyId || savingCoBrand}
          />
        </div>
        {/* Sin logo cargado no hay con qué reemplazar el de Beartrack: se
            deshabilita en vez de dejar entrar a un estado que el backend
            igualmente ignoraría. */}
        {!company?.logo && (
          <p className="text-[11px] text-fg-subtle mt-2">
            {t("Primero sube el logo de tu empresa para poder activarlo.")}
          </p>
        )}
        {/* El superadministrador del sistema abre esta pantalla sin empresa
            asociada, así que no hay a quién aplicarle la preferencia. */}
        {!companyId && (
          <p className="text-[11px] text-fg-subtle mt-2">
            {t("Esta opción se configura desde la cuenta de una empresa logística.")}
          </p>
        )}
        <p className="text-[11px] text-fg-subtle mt-2">
          {t("Los comercios que trabajan con más de un operador seguirán viendo el logo de Beartrack.")}
        </p>
      </SectionCard>
    </div>
  );
}
